import {
  updateCRM,
  compileSolidity,
  downloadProject,
} from "~/services/chat.services";
import { generateAudit, AuditType } from "~/services/event.services";
import {
  isCompilationErrorResponse,
  extractCompilationErrors,
} from "~/lib/utils";
import { t } from "i18next";
import type { FileNode } from "~/+types/project";
import useChatMessagesStore from "~/stores/chatMessagesStore";
import {
  isChatToStudioFirstGeneration,
  isWsProjectIdPertinent,
  normalizeChatMessage,
  normalizeProgressPercent,
  normalizeProjectId,
} from "~/lib/chatMessageSync";
import useCodeGeneratorStore from "~/stores/codeGeneratorStore";
import useCompilationStore from "~/stores/compilationStore";
import useUnitTestStore from "~/stores/unitTestStore";
import useDownloadStore from "~/stores/downloadStore";
import useNotificationStore from "~/stores/notificationStore";
import { useSherpaStudioStore } from "~/stores/sherpaStudioStore";
import { useSubscriptionStore } from "~/stores/subscriptionStore";
import type { UnitTestResult } from "~/services/unitTest.services";

type WsNotification = {
  type: string;
  message: any;
  connectionId?: string;
};

type WsHandlerRuntime = {
  auditorUserToken?: string;
};

type WsCaseHandler = (
  notification: WsNotification,
  runtime: WsHandlerRuntime,
) => void;

const normalizePath = (path?: string) => (path || "").replace(/^\/+/, "");
const toAbsolutePath = (path?: string) => {
  const normalized = normalizePath(path);
  return normalized ? `/${normalized}` : "/";
};
const getFileNameFromPath = (path?: string) => {
  const normalized = normalizePath(path);
  if (!normalized) return "";
  const segments = normalized.split("/").filter(Boolean);
  return segments[segments.length - 1] || "";
};

const updateFileNodeInTree = (
  nodes: FileNode[] | null,
  sourcePath: string,
  nextPath?: string,
  nextName?: string,
  nextContent?: string,
): FileNode[] | null => {
  if (!nodes) return nodes;

  const normalizedSource = normalizePath(sourcePath);
  if (!normalizedSource) return nodes;

  const walk = (items: FileNode[]): FileNode[] =>
    items.map((node) => {
      const normalizedNodePath = normalizePath(node.path);
      if (normalizedNodePath === normalizedSource) {
        const resolvedPath = nextPath
          ? toAbsolutePath(nextPath)
          : toAbsolutePath(node.path);
        const resolvedName =
          nextName || getFileNameFromPath(resolvedPath) || node.name;
        return {
          ...node,
          id: resolvedPath,
          path: resolvedPath,
          name: resolvedName,
          ...(typeof nextContent === "string" ? { content: nextContent } : {}),
        };
      }
      if (node.children?.length) {
        return {
          ...node,
          children: walk(node.children),
        };
      }
      return node;
    });

  return walk(nodes);
};

const getHistoryId = () => useChatMessagesStore.getState().historyId;
const getWsConnectionId = () => useSherpaStudioStore.getState().wsConnectionId;

/**
 * Extracts file paths from a "files deleted"-style WS message. The backend
 * isn't perfectly consistent across endpoints, so we accept any of:
 *   - { files: [{ path }] | string[] }
 *   - { paths: string[] }
 *   - { file: { path } | string }
 *   - { path: string }
 */
const extractDeletedPaths = (message: any): string[] => {
  if (!message || typeof message !== "object") return [];

  const collected: string[] = [];
  const push = (candidate: unknown) => {
    if (typeof candidate === "string" && candidate.length > 0) {
      collected.push(candidate);
    } else if (
      candidate &&
      typeof candidate === "object" &&
      typeof (candidate as { path?: unknown }).path === "string"
    ) {
      collected.push((candidate as { path: string }).path);
    }
  };

  if (Array.isArray(message.files)) {
    message.files.forEach(push);
  }
  if (Array.isArray(message.paths)) {
    message.paths.forEach(push);
  }
  if (message.file !== undefined) push(message.file);
  if (typeof message.path === "string") collected.push(message.path);

  return Array.from(new Set(collected.filter((p) => p && p.length > 0)));
};

const handleFilesDeleted: WsCaseHandler = (currentNotification) => {
  const paths = extractDeletedPaths(currentNotification.message);
  if (paths.length === 0) {
    console.warn(
      "[WebSocket] files-deleted message received but no paths extracted:",
      currentNotification,
    );
    return;
  }
  console.log("[WebSocket] removing files from tree:", paths);
  useSherpaStudioStore.getState().removeFilesFromTree(paths);
};

const isProjectIdPertinent = (message: any): boolean => {
  const historyId = getHistoryId();
  const studioProjectId = useSherpaStudioStore.getState().projectId;
  const incomingId = message?.chatId ?? message?.projectId;

  const pertinent = isWsProjectIdPertinent({
    historyId,
    studioProjectId,
    incomingId,
  });

  if (!pertinent) {
    console.warn(
      `[WebSocket] Message ignored — project ID mismatch: incoming=${incomingId}, historyId=${historyId}, studioProjectId=${studioProjectId}`,
    );
    return false;
  }

  // Align historyId with studio route when WS targets the open project but store lagged.
  const incoming = normalizeProjectId(incomingId);
  const activeHistory = normalizeProjectId(historyId);
  const routeProject = normalizeProjectId(studioProjectId ?? "");
  if (
    incoming &&
    routeProject &&
    incoming === routeProject &&
    activeHistory !== incoming
  ) {
    useChatMessagesStore
      .getState()
      .actions.setHistoryId(incoming, "wsMessageHandler.isProjectIdPertinent");
  }

  return true;
};
// Canonical registry for `projectStatus` values.
// Keep this object aligned with backend statuses:
// every supported `projectStatus` message must have a handler entry here.
const projectStatusHandlers: Record<string, WsCaseHandler> = {
  generatingCode: (notification) => {
    const chatMessagesState = useChatMessagesStore.getState();
    const chatMessageStoreActions = chatMessagesState.actions;
    const codeGeneratorStoreActions = useCodeGeneratorStore.getState().actions;
    const compilationActions = useCompilationStore.getState().actions;
    const hasReceivedResponse = chatMessagesState.hasReceivedAssistantResponse;

    if (!hasReceivedResponse) {
      chatMessageStoreActions.setPendingCodeGeneration(true);
    }
    chatMessageStoreActions.setIsGeneratingCode(true, "projectStatus");
    const isFirstGeneration = isChatToStudioFirstGeneration(
      chatMessagesState.hasGeneratedCode,
    );
    if (isFirstGeneration) {
      codeGeneratorStoreActions.setProgress(0, t("starting_code_generation"));
      if (hasReceivedResponse) {
        compilationActions.setCompilationResult({
          status: t("idle"),
          errors: "",
        });
      }
    } else {
      codeGeneratorStoreActions.clearLoadingState();
    }

    const historyId = getHistoryId();
    if (!historyId) {
      chatMessageStoreActions.setHistoryId(
        notification.message.chatId,
        "generatingCode",
      );
    }

    generateAudit(AuditType.kodeSherpaBeforeGeneration, {
      event: "KodeSherpa Before Generation",
      eventDescription:
        "A successfull KodeSherpa sent last message before generation.",
      eventLog: {
        wsConnectionId: getWsConnectionId(),
        KodeSherpaLastMessageSent: notification.message,
        KodeSherpaLastMessageSentDate: new Date().toJSON(),
      },
    });
  },

  codeGenerated: (notification) => {
    const chatMessagesState = useChatMessagesStore.getState();
    const chatMessageStoreActions = chatMessagesState.actions;
    const codeGeneratorStoreActions = useCodeGeneratorStore.getState().actions;
    const isFirstGeneration = isChatToStudioFirstGeneration(
      chatMessagesState.hasGeneratedCode,
    );
    const historyId = getHistoryId();
    const tokenUsage = useSubscriptionStore.getState().tokenUsage;
    chatMessageStoreActions.setLoadingStatusMessage(
      t("generated"),
      "projectStatus",
    );
    chatMessageStoreActions.setIsGeneratingCode(false, "projectStatus");
    if (isFirstGeneration) {
      codeGeneratorStoreActions.setProgress(100, t("generated"));
    } else {
      codeGeneratorStoreActions.clearLoadingState();
    }

    // Reload the project file tree from the backend so the Monaco editor
    // reflects the newly generated code. The old flow stored the code in
    // codeGeneratorStore.latestCode which Monaco never reads.
    useSherpaStudioStore.getState().reloadProjectFiles();

    generateAudit(AuditType.kodeSherpaGeneration, {
      event: "KodeSherpa generation",
      eventDescription: "A successfull KodeSherpa generation.",
      eventLog: { notification },
    });

    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    updateCRM({
      caller: "Kodesherpa Login",
      action: "general",
      properties: [
        { label: "AKB_LAST_GENERATION", value: now.toISOString() },
        {
          label: "AKB_REMAINING_SEATS",
          value: tokenUsage?.remaining ?? 0,
        },
      ],
    });
  },

  updatingCode: () => {
    const chatMessageStoreActions = useChatMessagesStore.getState().actions;
    (chatMessageStoreActions as any).setIsUpdatingCode(true, "projectStatus");
  },

  codeUpdated: () => {
    const chatMessageStoreActions = useChatMessagesStore.getState().actions;
    (chatMessageStoreActions as any).setIsUpdatingCode(false, "projectStatus");

    // Reload the project file tree from the backend so the Monaco editor
    // reflects the updated code.
    useSherpaStudioStore.getState().reloadProjectFiles();
  },

  generatingTests: () => {
    const unitTestStoreActions = useUnitTestStore.getState().actions;
    unitTestStoreActions.setIsGenerating(true);
  },

  testsGenerated: () => {
    const unitTestStoreActions = useUnitTestStore.getState().actions;
    unitTestStoreActions.setIsGenerating(false);
  },

  fixingErrors: () => {
    const compilationActions = useCompilationStore.getState().actions;
    compilationActions.setIsCompiling(true);
    compilationActions.setCompilationResult({
      status: t("fixing_errors_status"),
      errors: "",
    });
    compilationActions.setIsFixingErrors(true);
    compilationActions.setHasFixErrorsFailed(false);
  },

  errorsFixed: () => {
    const compilationActions = useCompilationStore.getState().actions;
    const historyId = getHistoryId();
    const wsConnectionId = getWsConnectionId();
    compilationActions.setIsCompiling(false);
    compilationActions.setIsFixingErrors(false);

    // Reload files so Monaco shows the fixed code, then re-compile
    const sherpaStore = useSherpaStudioStore.getState();
    sherpaStore.reloadProjectFiles().then(() => {
      const { activeFile, fileContents, projectId } =
        useSherpaStudioStore.getState();
      if (
        wsConnectionId &&
        historyId &&
        activeFile &&
        fileContents[activeFile]
      ) {
        compileSolidity(wsConnectionId, historyId).then(
          (compilationResult: any) => {
            if (compilationResult.status === t("not_compiled")) {
              compilationActions.setHasFixErrorsFailed(true);
            } else {
              compilationActions.setHasFixErrorsFailed(false);
            }
          },
        );
      }
    });
  },

  toolInvoked: (notification) => {
    const chatMessageStoreActions = useChatMessagesStore.getState().actions;
    const toolName = notification.message.toolInvoked;
    const silentTools = ["compileSolidity", "generateEnhancedTests"];
    if (!silentTools.includes(toolName)) {
      chatMessageStoreActions.setInvokedTool(toolName, "toolInvoked");
    }
  },

  compilingSolidity: () => {
    const compilationActions = useCompilationStore.getState().actions;
    compilationActions.setIsCompiling(true);
  },

  solidityCompiled: () => {
    const compilationActions = useCompilationStore.getState().actions;
    const sherpaStudioState = useSherpaStudioStore.getState();
    compilationActions.setIsCompiling(false);
    const isCompileAlreadyVisible =
      sherpaStudioState.isBottomPanelOpen &&
      sherpaStudioState.terminalActiveTab === "compile";
    if (!isCompileAlreadyVisible) {
      sherpaStudioState.setTerminalActiveTab("compile");
      if (!sherpaStudioState.isBottomPanelOpen) {
        sherpaStudioState.toggleBottomPanel();
      }
    }
  },

  testsExecutionStarted: async (notification) => {
    // Open the panel
    useSherpaStudioStore.getState().setTerminalActiveTab("test");
    useSherpaStudioStore.getState().setBottomPanel(true);
    const unitTestStore = useUnitTestStore.getState();

    // If there hasn't been any error already
    if (!(unitTestStore.error && unitTestStore.errorType === "execution")) {
      unitTestStore.actions.setIsGenerating(true);
      unitTestStore.actions.setTestPhase("execution");
      unitTestStore.actions.setProgress(
        0,
        t("executing_tests", "Executing tests..."),
      );
    }
  },

  testsExecutionCompleted: (notification) => {
    const compilationActions = useCompilationStore.getState().actions;
    const unitTestStoreActions = useUnitTestStore.getState().actions;
    const notificationStoreActions = useNotificationStore.getState().actions;
    const report = notification.message.executionReport;

    if (!report) {
      console.warn(
        "[WebSocket] projectStatus testsExecutionCompleted received but executionReport is missing",
      );
      unitTestStoreActions.setIsGenerating(false);
      unitTestStoreActions.setTestPhase(null);
      return;
    }

    if (isCompilationErrorResponse(report)) {
      const compilationErrors = extractCompilationErrors(report);
      compilationActions.setCompilationResult({
        status: "Not compiled",
        errors: compilationErrors,
      });
      compilationActions.setModalVisibility(true);
      unitTestStoreActions.setModalVisibility(false);
      unitTestStoreActions.setError(null);
      unitTestStoreActions.setIsGenerating(false);
      unitTestStoreActions.setTestPhase(null);
      return;
    }

    // Map flat properties to executionSummary when backend sends a flat execution report
    const mappedReport = { ...report } as UnitTestResult;
    if (!report.executionSummary) {
      mappedReport.executionSummary = {
        compilationSuccess: report.compilationSuccess ?? true,
        totalTests: report.totalTests ?? 0,
        passedTests: report.passedTests ?? 0,
        failedTests: report.failedTests ?? 0,
        skippedTests: report.skippedTests ?? 0,
        completionAttempts: report.completionAttempts ?? 0,
        successRate: report.successRate ?? 0,
        executionTime: report.executionTime ?? 0,
        coverageCollected: report.coverageCollected ?? false,
        coverage: report.coverage,
      };
    }

    unitTestStoreActions.setUnitTestResult(mappedReport as UnitTestResult);
    notificationStoreActions.clearAllNotifications();

    const sherpaStudioState = useSherpaStudioStore.getState();
    const isTestTabAlreadyVisible =
      sherpaStudioState.isBottomPanelOpen &&
      sherpaStudioState.terminalActiveTab === "test";
    if (!isTestTabAlreadyVisible) {
      sherpaStudioState.setTerminalActiveTab("test");
      if (!sherpaStudioState.isBottomPanelOpen) {
        sherpaStudioState.toggleBottomPanel();
      }
    }
  },
};

const projectErrorHandlers: Record<string, WsCaseHandler> = {
  chatError: () => {
    const chatMessageStoreActions = useChatMessagesStore.getState().actions;
    chatMessageStoreActions.setIsWaitingAnswer(false, "projectError chatError");
    chatMessageStoreActions.setInvokedTool("", "projectError chatError");
    chatMessageStoreActions.setPendingCodeGeneration(false);
  },
  testExecutionError: (notification) => {
    console.log("[WebSocket] testExecutionError -", notification.message.error);

    const unitTestStoreActions = useUnitTestStore.getState().actions;
    const sherpaStudioState = useSherpaStudioStore.getState();

    unitTestStoreActions.setGenerationError(
      notification.message?.error ||
        t(
          "error_occurred_executing_unit_tests",
          "An error occurred during test execution",
        ),
      "execution",
    );
    unitTestStoreActions.setProgress(
      0,
      t("error_occurred_executing_unit_tests", "Test execution failed"),
    );
    sherpaStudioState.setTerminalActiveTab("test");
    useSherpaStudioStore.getState().setBottomPanel(true);
  },
  testGenerationError: (notification) => {
    const unitTestStoreActions = useUnitTestStore.getState().actions;
    const sherpaStudioState = useSherpaStudioStore.getState();
    unitTestStoreActions.setGenerationError(
      notification.message?.error ||
        t(
          "error_occurred_generating_unit_tests",
          "An error occurred while generating tests",
        ),
      "generation",
    );
    unitTestStoreActions.setProgress(
      0,
      t("error_occurred_generating_unit_tests", "Test generation failed"),
    );
    sherpaStudioState.setTerminalActiveTab("test");
    if (!sherpaStudioState.isBottomPanelOpen) {
      sherpaStudioState.toggleBottomPanel();
    }
  },
};

const handleProjectStatus = (
  notification: WsNotification,
  runtime: WsHandlerRuntime,
) => {
  const status = notification.message.status;
  const handler = projectStatusHandlers[status];
  if (handler) {
    handler(notification, runtime);
  } else {
    console.warn("[WebSocket] No handler for projectStatus:", status);
  }
};

const handleProjectError = (notification: WsNotification) => {
  const errorType = notification.message?.status;
  const handler = projectErrorHandlers[errorType];
  if (handler) {
    handler(notification, {});
  } else {
    console.warn("[WebSocket] No handler for projectError:", errorType);
  }
};

export const handleWsNotification = (
  notification: WsNotification,
  runtime: WsHandlerRuntime = {},
) => {
  console.log("[WebSocket] ", notification);
  if (
    notification.type !== "initAuditor" &&
    !isProjectIdPertinent(notification.message)
  ) {
    return;
  }

  // Canonical registry for top-level websocket message types.
  // This object must contain all supported backend message types.
  // If backend adds a new type, add the corresponding handler here.
  const wsTypeHandlers: Record<string, WsCaseHandler> = {
    initAuditor: (currentNotification) => {
      useSherpaStudioStore
        .getState()
        .setWsConnectionId(currentNotification.connectionId || "");
    },

    assistantResponseMsg: (currentNotification) => {
      const chatMessagesState = useChatMessagesStore.getState();
      const codeGeneratorState = useCodeGeneratorStore.getState();
      const chatMessageStoreActions = chatMessagesState.actions;
      const codeGeneratorStoreActions = codeGeneratorState.actions;
      const compilationActions = useCompilationStore.getState().actions;
      const response = currentNotification.message;
      chatMessageStoreActions.setHistoryId(
        response.chatId,
        "handleMessageSubmit",
      );
      chatMessageStoreActions.setIsWaitingAnswer(false, "handleMessageSubmit");
      chatMessageStoreActions.setInvokedTool("", "assistantResponseMsg");
      const normalized = normalizeChatMessage(response.gptResponse, "assistant");
      const lastMessage =
        chatMessagesState.messages[chatMessagesState.messages.length - 1];
      const isDuplicateOfLast =
        lastMessage &&
        lastMessage.role === normalized.role &&
        String(lastMessage.content ?? "").trim() ===
          String(normalized.content ?? "").trim();

      if (!isDuplicateOfLast) {
        chatMessageStoreActions.addMessage(
          normalized,
          "Add Assistant Response",
        );
      }
      chatMessageStoreActions.setHasReceivedAssistantResponse(true);

      const pendingGeneration = chatMessagesState.pendingCodeGeneration;
      if (pendingGeneration) {
        chatMessageStoreActions.setPendingCodeGeneration(false);
        const isGenerating = chatMessagesState.isGeneratingCode;
        if (
          isGenerating &&
          isChatToStudioFirstGeneration(chatMessagesState.hasGeneratedCode)
        ) {
          codeGeneratorStoreActions.setProgress(
            codeGeneratorState.loadingProgress || 0,
            codeGeneratorState.loadingMessage || t("starting_code_generation"),
          );
          compilationActions.setCompilationResult({
            status: t("idle"),
            errors: "",
          });
        }
      }
    },

    generationProgression: (currentNotification) => {
      const chatMessagesState = useChatMessagesStore.getState();
      if (
        !isChatToStudioFirstGeneration(chatMessagesState.hasGeneratedCode)
      ) {
        return;
      }

      const chatMessageStoreActions = chatMessagesState.actions;
      const codeGeneratorStoreActions =
        useCodeGeneratorStore.getState().actions;
      const progress = normalizeProgressPercent(
        currentNotification.message.progress,
      );
      const progressMessage =
        currentNotification.message.message ||
        t("starting_code_generation");

      if (!chatMessagesState.isGeneratingCode) {
        if (!chatMessagesState.hasReceivedAssistantResponse) {
          chatMessageStoreActions.setPendingCodeGeneration(true);
        }
        chatMessageStoreActions.setIsGeneratingCode(
          true,
          "generationProgression",
        );
      }

      codeGeneratorStoreActions.setProgress(progress, progressMessage);
    },

    testGenerationProgression: (currentNotification) => {
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      unitTestStoreActions.setTestPhase("generation");
      unitTestStoreActions.setProgress(
        currentNotification.message.progress,
        currentNotification.message.message,
      );
    },

    testExecutionProgression: (currentNotification) => {
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      unitTestStoreActions.setTestPhase("execution");
      unitTestStoreActions.setProgress(
        currentNotification.message.progress,
        currentNotification.message.message,
      );
    },

    testsExecutionStarted: (currentNotification) => {
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      unitTestStoreActions.setIsGenerating(true);
      unitTestStoreActions.setTestPhase("execution");
      unitTestStoreActions.setProgress(
        0,
        t("executing_tests", "Executing tests..."),
      );
    },

    generationSolError: (currentNotification) => {
      const chatMessageStoreActions = useChatMessagesStore.getState().actions;
      const codeGeneratorStoreActions =
        useCodeGeneratorStore.getState().actions;
      const compilationActions = useCompilationStore.getState().actions;
      chatMessageStoreActions.setLoadingStatusMessage(
        t("error_during_generation"),
        "generationSolError",
      );
      codeGeneratorStoreActions.clearLoadingState();
      chatMessageStoreActions.setIsGeneratingCode(false, "generationSolError");
      chatMessageStoreActions.setIsEditorVisible(false);
      compilationActions.setCompilationResult({
        status: t("not_compiled"),
        errors: t("generation_failed"),
      });
      generateAudit(AuditType.kodeSherpaGenerationError, {
        event: "KodeSherpa generation error",
        eventDescription: "An error occured during the KodeSherpa generation.",
        eventLog: {
          wsConnectionId: getWsConnectionId(),
          notification: currentNotification,
        },
      });
    },

    generationTest: (currentNotification) => {
      const compilationActions = useCompilationStore.getState().actions;
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      const notificationStoreActions = useNotificationStore.getState().actions;
      const historyId = getHistoryId();
      const wsConnectionId = getWsConnectionId();
      const testResult = currentNotification.message;
      if (isCompilationErrorResponse(testResult)) {
        const compilationErrors = extractCompilationErrors(testResult);
        compilationActions.setCompilationResult({
          status: "Not compiled",
          errors: compilationErrors,
        });
        compilationActions.setModalVisibility(true);
        unitTestStoreActions.setModalVisibility(false);
        unitTestStoreActions.setError(null);
        unitTestStoreActions.setIsGenerating(false);
        unitTestStoreActions.setTestPhase(null);
        return;
      }

      unitTestStoreActions.setTestPhase("generation");
      unitTestStoreActions.setUnitTestResult(testResult);
      notificationStoreActions.clearAllNotifications();

      const isDownloadInProgress =
        useDownloadStore.getState().isGenerateTestsDownloadInProgress;
      if (isDownloadInProgress) {
        downloadProject(historyId, true, false)
          .then((response) => {
            if (!response) {
              console.error(t("failed_to_generate_project"));
              return;
            }

            const byteCharacters = atob(response);
            const byteArrays = [];
            for (let i = 0; i < byteCharacters.length; i++) {
              byteArrays.push(byteCharacters.charCodeAt(i));
            }
            const blob = new Blob([new Uint8Array(byteArrays)], {
              type: "application/zip",
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "project.zip";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            generateAudit(AuditType.kodeSherpaDownload, {
              event: "KodeSherpa Download",
              eventDescription: "User downloaded project",
              eventLog: {
                wsConnectionId,
                option: "generate-tests",
              },
            });

            useDownloadStore.getState().actions.setDownloadCompleted(true);
          })
          .catch((error) => {
            console.error(
              "Error during download after test generation:",
              error,
            );
          })
          .finally(() => {
            useDownloadStore
              .getState()
              .actions.setIsGenerateTestsDownloadInProgress(false);
          });
      }
    },

    generationTestError: (currentNotification) => {
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      unitTestStoreActions.setGenerationError(
        currentNotification.message.error ||
          t("error_occurred_generating_unit_tests"),
        "generation",
      );
      const isDownloadInProgressError =
        useDownloadStore.getState().isGenerateTestsDownloadInProgress;
      if (isDownloadInProgressError) {
        useDownloadStore
          .getState()
          .actions.setIsGenerateTestsDownloadInProgress(false);
        useDownloadStore.getState().actions.setShouldCloseDownloadModal(true);
      }
    },

    testsExecutionError: (currentNotification) => {
      console.error(
        "[WebSocket] Received testsExecutionError:",
        currentNotification.message,
      );
      const unitTestStoreActions = useUnitTestStore.getState().actions;
      unitTestStoreActions.setGenerationError(
        currentNotification.message.error ||
          t(
            "error_occurred_executing_unit_tests",
            "An error occurred during test execution",
          ),
        "execution",
      );
    },

    compilationResult: (currentNotification) => {
      const compilationActions = useCompilationStore.getState().actions;
      compilationActions.setCompilationResult({
        status: currentNotification.message.status,
        errors: currentNotification.message.errors,
      });
    },

    projectStatus: (currentNotification, currentRuntime) => {
      console.log(
        "[WebSocket] projectStatus -",
        currentNotification.message.status,
      );
      handleProjectStatus(currentNotification, currentRuntime);
    },

    projectError: (currentNotification) => {
      console.log(
        "[WebSocket] projectError -",
        currentNotification.message.error,
      );
      handleProjectError(currentNotification);
    },

    filesCreated: (currentNotification) => {
      const sherpaStudioStoreActions = useSherpaStudioStore.getState();
      const files = currentNotification.message?.files;
      if (Array.isArray(files) && files.length > 0) {
        sherpaStudioStoreActions.upsertFilesToTree(files);
      }
    },

    filesDeleted: handleFilesDeleted,
    deletedFiles: handleFilesDeleted,
    deleteFile: handleFilesDeleted,
    deleteFiles: handleFilesDeleted,
    fileDeleted: handleFilesDeleted,

    filesUpdated: (currentNotification) => {
      const incomingFiles = currentNotification.message?.files;
      if (!Array.isArray(incomingFiles) || incomingFiles.length === 0) {
        return;
      }

      useSherpaStudioStore.setState((state) => {
        let nextTree = state.fileTree;
        let nextFileContents = { ...state.fileContents };
        let nextOpenFiles = [...state.openFiles];
        let nextActiveFile = state.activeFile;
        let nextOpenTabs = [...state.openTabs];
        let nextActiveTabId = state.activeTabId;
        let nextSelectedFileId = state.selectedFileId;

        for (const file of incomingFiles) {
          const sourcePath = toAbsolutePath(file?.path);
          const targetPath = file?.newPath
            ? toAbsolutePath(file.newPath)
            : sourcePath;
          const targetName = file?.newName || getFileNameFromPath(targetPath);
          const nextContent =
            typeof file?.newContent === "string" ? file.newContent : undefined;

          if (!sourcePath || sourcePath === "/") continue;

          nextTree = updateFileNodeInTree(
            nextTree,
            sourcePath,
            targetPath,
            file?.newName,
            nextContent,
          );

          const sourceContent = nextFileContents[sourcePath];
          if (sourcePath !== targetPath) {
            delete nextFileContents[sourcePath];
            if (typeof sourceContent === "string") {
              nextFileContents[targetPath] = sourceContent;
            }
          }
          if (typeof nextContent === "string") {
            nextFileContents[targetPath] = nextContent;
          }

          nextOpenFiles = nextOpenFiles.map((path) =>
            path === sourcePath ? targetPath : path,
          );
          if (nextActiveFile === sourcePath) {
            nextActiveFile = targetPath;
          }

          nextOpenTabs = nextOpenTabs.map((tab) => {
            if (tab.filePath !== sourcePath) return tab;
            return {
              ...tab,
              id: targetPath,
              fileId: targetPath,
              filePath: targetPath,
              fileName: targetName || tab.fileName,
            };
          });
          if (nextActiveTabId === sourcePath) {
            nextActiveTabId = targetPath;
          }
          if (
            nextSelectedFileId === sourcePath ||
            nextSelectedFileId === file?.path
          ) {
            nextSelectedFileId = targetPath;
          }
        }

        return {
          fileTree: nextTree,
          fileContents: nextFileContents,
          openFiles: nextOpenFiles,
          activeFile: nextActiveFile,
          openTabs: nextOpenTabs,
          activeTabId: nextActiveTabId,
          selectedFileId: nextSelectedFileId,
          currentProject: state.currentProject
            ? {
                ...state.currentProject,
                files: nextTree || [],
              }
            : state.currentProject,
        };
      });
    },

    notification: (currentNotification) => {
      const unitTestState = useUnitTestStore.getState();
      const notificationStoreActions = useNotificationStore.getState().actions;
      const {
        status: notificationStatus,
        message: notificationMessage,
        info,
      } = currentNotification.message;

      const isGeneratingTests = unitTestState.isGenerating;
      if (notificationStatus === "error" && isGeneratingTests) {
        if (info) {
          console.warn(
            `[Notification WARNING during test generation]: ${info}`,
          );
        }
        return;
      }

      notificationStoreActions.showNotification(
        notificationStatus,
        t(notificationMessage),
      );
      if (info) {
        console.log(
          `[Notification ${notificationStatus.toUpperCase()}]: ${info}`,
        );
      }
      if (notificationStatus === "error") {
        generateAudit(AuditType.kodeSherpaError, {
          event: "KodeSherpa error",
          eventDescription: "An error occured during the KodeSherpa session.",
          eventLog: { notification: currentNotification },
        });
      }
    },

    PROCESSING_ERROR: (currentNotification) => {
      const chatMessageStoreActions = useChatMessagesStore.getState().actions;
      const codeGeneratorStoreActions =
        useCodeGeneratorStore.getState().actions;
      chatMessageStoreActions.setLoadingStatusMessage(
        currentNotification.message.message,
        "PROCESSING_ERROR",
      );
      chatMessageStoreActions.setIsGeneratingCode(false, "PROCESSING_ERROR");
      (chatMessageStoreActions as any).setIsUpdatingCode(
        false,
        "PROCESSING_ERROR",
      );
      chatMessageStoreActions.setIsWaitingAnswer(false, "PROCESSING_ERROR");
      codeGeneratorStoreActions.clearLoadingState();
    },

    UNAUTHORIZED: () => {},
    EXECUTION_LIMIT: () => {},
    ERR01: () => {},

    Loading: (currentNotification) => {
      const chatMessageStoreActions = useChatMessagesStore.getState().actions;
      chatMessageStoreActions.setLoadingStatusMessage(
        currentNotification.message.text,
        "Loading",
      );
    },

    finishReport: (_currentNotification, currentRuntime) => {
      const chatMessageStoreActions = useChatMessagesStore.getState().actions;
      chatMessageStoreActions.setLoadingStatusMessage("", "finishReport");
      const HOME_URL = import.meta.env.VITE_HOME_URL;
      window.open(
        `${HOME_URL}external/auditor/history/${currentRuntime.auditorUserToken}`,
        "_blank",
      );
    },
  };

  const handler = wsTypeHandlers[notification.type];
  if (handler) {
    console.log("[WebSocket]", notification.type);
    handler(notification, runtime);
  } else {
    console.warn("[WebSocket] No handler for message type:", notification.type);
  }
};
